// useIntlAccaQualifier — window + active signals + watchlist for the
// Elite-tier Qualifier Intel surface. Backed live by:
//   • intl_acca_qualifier_windows — VIEW over intl_acca_tournaments + counts
//   • intl_acca_qualifier_signals — populated by the daily orchestrator
//     intl_acca_generate_qualifier_signals (IFPE fair price vs market odds).
//
// DEFENSIVE / HONEST-EMPTY: signals only exist once the intl-acca pipeline
// (IFPE outputs + line snapshots) has run for a window; until then this renders
// the honest-empty state. Every query is still caught and degraded to null/[]
// — NEVER throw, NEVER fabricate.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import type {
  QualifierSignal,
  QualifierWatchlistItem,
  QualifierWindow,
} from "../db/intl-acca-types-extended.ts";

export interface IntlAccaQualifierVM {
  loading: boolean;
  error: string | null;
  window: QualifierWindow | null;
  signals: QualifierSignal[];
  watchlist: QualifierWatchlistItem[];
  refetch: () => Promise<void>;
}

export function useIntlAccaQualifier(windowId: string): IntlAccaQualifierVM {
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [s, setS] = useState<Omit<IntlAccaQualifierVM, "refetch">>({
    loading: true, error: null, window: null, signals: [], watchlist: [],
  });

  const fetchAll = useCallback(async () => {
    if (!windowId) {
      setS({ loading: false, error: null, window: null, signals: [], watchlist: [] });
      return;
    }
    setS((p) => ({ ...p, loading: true, error: null }));

    let win: QualifierWindow | null = null;
    let signals: QualifierSignal[] = [];
    let watchlist: QualifierWatchlistItem[] = [];

    // Future tables — catch + degrade so the page shows honest-empty.
    try {
      const wRes = await supabase.from("intl_acca_qualifier_windows")
        .select("*").eq("id", windowId).maybeSingle();
      if (!wRes.error) win = (wRes.data as QualifierWindow | null) ?? null;
    } catch {
      win = null;
    }

    try {
      const sgRes = await supabase.from("intl_acca_qualifier_signals")
        .select("*").eq("window_id", windowId).eq("flag", "ACTIVE")
        .order("edge_pp", { ascending: false });
      if (!sgRes.error) signals = (sgRes.data ?? []) as QualifierSignal[];
    } catch {
      signals = [];
    }

    try {
      const wlRes = await supabase.from("intl_acca_qualifier_signals")
        .select("match_label, flag, reasoning_note")
        .eq("window_id", windowId)
        .in("flag", ["SPECULATIVE", "NEUTRAL"]);
      if (!wlRes.error && Array.isArray(wlRes.data)) {
        watchlist = (wlRes.data as Array<{
          match_label: string;
          flag: QualifierWatchlistItem["flag"];
          reasoning_note: string;
        }>).map((r) => ({
          match_label: r.match_label,
          flag: r.flag,
          note: r.reasoning_note,
        }));
      }
    } catch {
      watchlist = [];
    }

    setS({ loading: false, error: null, window: win, signals, watchlist });
  }, [supabase, windowId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
